import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { transportAPI } from '../../services/api';
import AppNavbar from '../../components/AppNavbar';
import SiteFooter from '../../components/SiteFooter';
import MobileBottomNav from '../../components/MobileBottomNav';

const CARGO_LABELS = {
  furniture: 'أثاث', building_materials: 'مواد بناء', commercial_goods: 'بضائع تجارية',
  parcels: 'طرود', equipment: 'معدات', food_items: 'مواد غذائية', vehicle: 'سيارة', other: 'أخرى',
};

const REQUEST_STATUS_CFG = {
  open_for_quotes: { label: 'بانتظار العروض', color: 'bg-amber-100 text-amber-700' },
  quote_selected:  { label: 'تم اختيار عرض', color: 'bg-blue-100 text-blue-700' },
  cancelled:       { label: 'ملغى', color: 'bg-red-100 text-red-700' },
  expired:         { label: 'منتهي', color: 'bg-gray-100 text-gray-500' },
};

const ORDER_STATUS_CFG = {
  payment_pending:   { label: 'بانتظار الدفع', color: 'bg-amber-100 text-amber-700' },
  payment_submitted: { label: 'إيصال قيد المراجعة', color: 'bg-amber-100 text-amber-700' },
  payment_confirmed: { label: 'بانتظار تعيين مركبة', color: 'bg-blue-100 text-blue-700' },
  vehicle_assigned:  { label: 'بانتظار الاستلام', color: 'bg-indigo-100 text-indigo-700' },
  picked_up:         { label: 'تم الاستلام', color: 'bg-purple-100 text-purple-700' },
  in_transit:        { label: 'في الطريق', color: 'bg-purple-100 text-purple-700' },
  delivered:         { label: 'تم التسليم', color: 'bg-emerald-100 text-emerald-700' },
  completed:         { label: 'مكتمل', color: 'bg-emerald-100 text-emerald-700' },
  cancelled:         { label: 'ملغى', color: 'bg-red-100 text-red-700' },
};

const RequestCard = ({ req, navigate }) => {
  const pendingQuotes = (req.quotes || []).filter((q) => q.status === 'pending').length;
  const target = req.order ? `/transport/orders/${req.order.id}` : `/transport/requests/${req.id}`;
  const statusCfg = req.order
    ? (ORDER_STATUS_CFG[req.order.status] || { label: req.order.status, color: 'bg-gray-100 text-gray-600' })
    : (REQUEST_STATUS_CFG[req.status] || { label: req.status, color: 'bg-gray-100 text-gray-600' });

  return (
    <button onClick={() => navigate(target)} className="w-full text-right bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:border-blue-200 transition-colors">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${statusCfg.color}`}>{statusCfg.label}</span>
        <span className="font-mono text-xs font-bold text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1">
          #{req.order?.orderNumber || req.requestNumber}
        </span>
      </div>
      <p className="font-bold text-gray-800 mb-1">{req.fromLabel} ← {req.toLabel}</p>
      <p className="text-xs text-gray-500 mb-2">{CARGO_LABELS[req.cargoCategory] || req.cargoCategory}{req.weightKg ? ` · ${req.weightKg} كجم` : ''}</p>
      {!req.order && req.status === 'open_for_quotes' && (
        <p className="text-xs font-semibold text-blue-700">
          {pendingQuotes > 0 ? `📩 ${pendingQuotes} عرض بانتظار مراجعتك` : 'بانتظار عروض المزودين...'}
        </p>
      )}
      {req.order && <p className="text-sm font-black text-emerald-600">{req.order.price} ج.س</p>}
    </button>
  );
};

const TransportRequestsPage = () => {
  const navigate = useNavigate();
  const isLoggedIn = !!JSON.parse(localStorage.getItem('passenger_user') || 'null')?.id;
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);

  const fetchRequests = useCallback(async () => {
    if (!isLoggedIn) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await transportAPI.getMyRequests();
      setRequests(res.data.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [isLoggedIn]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);
  useEffect(() => { if (!isLoggedIn) navigate('/login?from=/transport'); }, [isLoggedIn, navigate]);

  if (!isLoggedIn) return null;

  return (
    <div dir="rtl" className="min-h-screen bg-[#F5F9FF] pb-16">
      <AppNavbar />
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-black text-[#1E3A8A]">🚚 نقل البضائع</h1>
            <p className="text-sm text-gray-600 mt-0.5">فان، بيكب، شاحنة، تريلا — اطلب واختر من بين عروض مزودين حقيقيين</p>
          </div>
          <button onClick={() => navigate('/transport/new')} className="bg-[#1E40AF] hover:bg-[#1D4ED8] text-white text-sm font-bold px-5 py-2.5 rounded-xl transition-all">
            + طلب نقل جديد
          </button>
        </div>

        {!isLoggedIn ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
            <p className="text-gray-600 font-semibold mb-4">سجّل الدخول لعرض طلباتك</p>
            <button onClick={() => navigate('/login?from=/transport')} className="bg-[#1E40AF] text-white font-bold px-6 py-2.5 rounded-xl">تسجيل الدخول</button>
          </div>
        ) : loading ? (
          <div className="text-center py-16 text-gray-400">جاري التحميل...</div>
        ) : requests.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
            <div className="text-5xl mb-3 opacity-60">🚚</div>
            <p className="text-gray-600 font-semibold">لا توجد طلبات نقل بعد</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((r) => <RequestCard key={r.id} req={r} navigate={navigate} />)}
          </div>
        )}
      </div>
      <div className="hidden md:block"><SiteFooter navigate={navigate} /></div>
      <MobileBottomNav />
    </div>
  );
};

export default TransportRequestsPage;
